import { useCallback } from 'react';
import { useShowConfirm } from './useShowConfirm';
import { useI18n } from './useI18n';

export interface UnsavedChangesParams {
  onCancel?: () => void;
  onExit: () => void;
}

/**
 * This hook allows reusing the unsaved changes confirmation in different scenarios
 * - We use the UnsavedChangesDialog component for router-level warnings
 *
 * The confirmation is built on top of useShowConfirm to reuse dialog, styles,
 * and vendor customizations. Think of it as a "specialized" version of the
 * confirmation dialog and use it consistently across the product.
 *
 * @waring consistent UX! please avoid creating custom messages
 */
export const useUnsavedChangesConfirm = () => {
  const { t } = useI18n();
  const showConfirm = useShowConfirm();

  const showUnsavedChangesDialog = useCallback(
    ({ onExit, onCancel = () => {} }: UnsavedChangesParams) =>
      showConfirm({
        allowClose: false,
        cancellationText: t('confirmation_dialog.cancel'),
        confirmationText: t('confirmation_dialog.exit_without_saving'),
        contentProps: { sx: { pb: 3 } },
        description: t('confirmation_dialog.unsaved_changes'),
        disablePortal: true,
        title: t('confirmation_dialog.title'),
        useErrorColor: true,
      }).then(onExit, onCancel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return showUnsavedChangesDialog;
};
